import { CalendarEntry } from "Components/Calendar/CalendarEntryHelper";
import { MinuteRange } from "Components/WorkingHours/types/MinuteRange";
import { SchedulePhase } from "domain-entities";
import moment, { Moment } from "moment";
import { useEffect, useRef, useState } from "react";
import { useDrop } from "react-dnd";
import { addCalendarEntriesToDayDropAreaPoints } from "./helpers/addCalendarEntriesToDayDropAreaPoints";
import { cloneDropAreaPoints } from "./helpers/cloneDropAreaPoints";
import { getDayDropAreas, getDayDropAreasWithHourSplitting } from "./helpers/getDayDropAreas";
import { getUnblockedDayDropAreaPoints } from "./helpers/getUnblockedDayDropAreaPoints";
import { getWeekDropAreaPoints } from "./helpers/getWeekDropAreaPoints";
import { DraggedItem } from "./types/DraggedItem";
import { DropArea } from "./types/DropArea";

/**
 * We are using undefined instead of optional fields
 * to replace them with firestore.FieldValue.delete() afterwards!
 */
export interface CalendarEntryUpdate {
	userId: string | undefined;
	resourceId: string | undefined;
}

interface CalendarEntrySet {
	calendarEntries: CalendarEntry[];
	typeId?: string;
	isPlaceholder?: boolean;
}

export function getMonthSchedulePhaseDropAreas(
	projectId: string,
	schedulePhases: SchedulePhase[],
	startOfCalendar: Moment,
	weeksLength: number,
	enabledWorkingDaysLength: number
) {
	const weeks = Array(weeksLength).fill(null);
	const days = Array(enabledWorkingDaysLength).fill(null);

	return weeks.reduce<DropArea[]>((result, _, weekIndex) => {
		const unblockedDays = days.map((_, dayIndex) => {
			const day = startOfCalendar
				.clone()
				.add(weekIndex, "weeks")
				.add(dayIndex, "day");

			return schedulePhases.some((phase) => {
				const startDate = moment.unix(phase.startDate);
				const endDate = moment.unix(phase.endDate);

				return startDate.isSameOrBefore(day) && endDate.isSameOrAfter(day);
			})
				? null
				: day;
		});

		let previousDropArea: DropArea | null = null;

		unblockedDays.forEach((unblockedDay) => {
			if (unblockedDay) {
				if (previousDropArea) {
					previousDropArea.end = unblockedDay;
				} else {
					previousDropArea = {
						projectId,
						start: unblockedDay,
						end: unblockedDay,
					};

					result.push(previousDropArea);
				}
			} else {
				previousDropArea = null;
			}
		});

		return result;
	}, []);
}

export function getWeekSchedulePhaseDropArea(
	day: Moment,
	schedulePhases: SchedulePhase[],
	projectId: string
) {
	for (const schedulePhase of schedulePhases) {
		const start = moment.unix(schedulePhase.startDate);
		const end = moment.unix(schedulePhase.endDate);

		if (start.isSameOrBefore(day) && end.isSameOrAfter(day)) {
			return;
		}
	}

	const dropArea: DropArea = {
		start: day,
		end: day,
		projectId,
	};

	return dropArea;
}

export function getWeekCalendarEntriesDropAreas(
	startOfWeek: Moment,
	calendarEntries: CalendarEntry[],
	scheduleEntryTypeIds: string[],
	visibleMinutes: MinuteRange,
	workingMinutes: MinuteRange
) {
	const dropAreas: DropArea[] = [];
	const weekDropAreaPoints = getWeekDropAreaPoints(
		visibleMinutes,
		workingMinutes
	);

	const calendarEntrySets: CalendarEntrySet[] = [
		{
			calendarEntries: calendarEntries.filter((entry) => !!entry.projectId),
		},
	];

	scheduleEntryTypeIds.forEach((typeId) => {
		const entries = calendarEntries.filter((entry) => entry.typeId === typeId);

		if (entries.length) {
			calendarEntrySets.push({
				calendarEntries: entries,
				typeId,
			});
		}
	});

	calendarEntrySets.push({
		calendarEntries: [],
		isPlaceholder: true,
	});

	calendarEntrySets.forEach((set) => {
		for (let weekday = 0; weekday <= 6; weekday++) {
			const day = startOfWeek.clone().add(weekday, "days");
			let dayDropAreaPoints = cloneDropAreaPoints(weekDropAreaPoints);

			addCalendarEntriesToDayDropAreaPoints(
				day,
				dayDropAreaPoints,
				set.calendarEntries,
				visibleMinutes
			);

			dayDropAreaPoints = getUnblockedDayDropAreaPoints(
				day,
				dayDropAreaPoints,
				set.calendarEntries
			);

			dayDropAreaPoints.sort((a, b) => a.minute - b.minute);

			const dayDropAreas = getDayDropAreas(day, dayDropAreaPoints);

			if (set.typeId || set.isPlaceholder) {
				dayDropAreas.forEach((dropArea) => {
					dropArea.typeId = set.typeId;
					dropArea.isPlaceholder = set.isPlaceholder;
				});
			}

			dropAreas.push(...dayDropAreas);
		}
	});

	return dropAreas;
}

export function getAcceptedDropTypes(
	dropArea: DropArea,
	scheduleEntryTypeIds: string[],
	categoryScheduleEntryTypeIds: string[]
) {
	if (dropArea.isPlaceholder) {
		return categoryScheduleEntryTypeIds.filter(
			(id) => !scheduleEntryTypeIds.includes(id)
		);
	} else {
		return [dropArea.typeId || "project"];
	}
}

export function getDropAreaViewKey(
	dropArea: DropArea,
	acceptedDropTypes: string[]
) {
	return [
		dropArea.start.unix(),
		dropArea.end.unix(),
		dropArea.projectId,
		dropArea.typeId,
		dropArea.isPlaceholder,
		...acceptedDropTypes,
	].join("-");
}

export function isSameRow(
	calendarEntry?: Partial<CalendarEntry>,
	calendarEntryUpdate?: CalendarEntryUpdate
) {
	if (calendarEntry && calendarEntryUpdate) {
		return (
			(calendarEntry.userId !== undefined &&
				calendarEntry.userId === calendarEntryUpdate.userId) ||
			(calendarEntry.resourceId !== undefined &&
				calendarEntry.resourceId === calendarEntryUpdate.resourceId)
		);
	}

	return false;
}

export function useDropArea(
	acceptedDropTypes: string[],
	scheduleEntryUpdate?: CalendarEntryUpdate,
	updateScheduleEntry?: (calendarEntry: CalendarEntry) => Promise<void>,
	addScheduleEntry?: (calendarEntry: CalendarEntry) => Promise<void>
) {
	const [isHovered, setIsHovered] = useState(false);
	const isHoverAllowedRef = useRef<boolean>();

	// Chrome dnd does not move the cursor to the correct position after a drop.
	// This effect will avoid flickering.
	// https://github.com/react-dnd/react-dnd/issues/1527
	useEffect(() => {
		setTimeout(() => {
			isHoverAllowedRef.current = true;
		});
	}, []);

	const [{ isDroppingAllowed, isDraggingOverAccepted, draggedItem }, drop] =
		useDrop({
			accept: acceptedDropTypes,
			drop: (draggedItem: DraggedItem) => {
				const isDroppingInSameRow = isSameRow(
					draggedItem.calendarEntry,
					scheduleEntryUpdate
				);

				if (draggedItem.isAddingNew || !isDroppingInSameRow) {
					addScheduleEntry && addScheduleEntry(draggedItem.calendarEntry);
				} else {
					updateScheduleEntry && updateScheduleEntry(draggedItem.calendarEntry);
				}
			},
			collect: (monitor) => {
				const draggedItem: DraggedItem | null = monitor.getItem();
				const isDragging = !!draggedItem?.type && !monitor.didDrop();
				const isDroppingAllowed = isDragging && monitor.canDrop();
				const isDraggingOverAccepted = isDroppingAllowed && monitor.isOver();

				if (isHoverAllowedRef.current !== undefined) {
					isHoverAllowedRef.current = !isDragging;
				}

				return {
					isDroppingAllowed,
					isDraggingOverAccepted,
					draggedItem: isDragging ? draggedItem : null,
				};
			},
		});

	return {
		isVisible: draggedItem ? isDraggingOverAccepted : isHovered,
		isDroppingAllowed,
		draggedItem,
		drop,
		setIsHovered: (nextIsHovered: boolean) => {
			if (nextIsHovered) {
				isHoverAllowedRef.current && setIsHovered(true);
			} else {
				setIsHovered(false);
			}
		},
	};
}

export function getDaySchedulePhaseDropArea(
	day: Moment,
	schedulePhases: SchedulePhase[],
	projectId: string
) {
	for (const schedulePhase of schedulePhases) {
		const start = moment.unix(schedulePhase.startDate);
		const end = moment.unix(schedulePhase.endDate);

		if (start.isSameOrBefore(day) && end.isSameOrAfter(day)) {
			return;
		}
	}

	const dropArea: DropArea = {
		start: day,
		end: day,
		projectId,
	};

	return dropArea;
}

export function getDayCalendarEntriesDropAreas(
	startOfDay: Moment,
	calendarEntries: CalendarEntry[],
	scheduleEntryTypeIds: string[],
	visibleMinutes: MinuteRange,
	workingMinutes: MinuteRange
) {
	const dropAreas: DropArea[] = [];
	const weekDropAreaPoints = getWeekDropAreaPoints(
		visibleMinutes,
		workingMinutes
	);

	const calendarEntrySets: CalendarEntrySet[] = [
		{
			calendarEntries: calendarEntries.filter((entry) => !!entry.projectId),
		},
	];

	scheduleEntryTypeIds.forEach((typeId) => {
		const entries = calendarEntries.filter((entry) => entry.typeId === typeId);

		if (entries.length) {
			calendarEntrySets.push({
				calendarEntries: entries,
				typeId,
			});
		}
	});

	calendarEntrySets.push({
		calendarEntries: [],
		isPlaceholder: true,
	});

	calendarEntrySets.forEach((set) => {
		const day = startOfDay.clone();
		let dayDropAreaPoints = cloneDropAreaPoints(weekDropAreaPoints);

		addCalendarEntriesToDayDropAreaPoints(
			day,
			dayDropAreaPoints,
			set.calendarEntries,
			visibleMinutes
		);

		dayDropAreaPoints = getUnblockedDayDropAreaPoints(
			day,
			dayDropAreaPoints,
			set.calendarEntries
		);

		dayDropAreaPoints.sort((a, b) => a.minute - b.minute);

		const dayDropAreas = getDayDropAreasWithHourSplitting(day, dayDropAreaPoints);

		if (set.typeId || set.isPlaceholder) {
			dayDropAreas.forEach((dropArea) => {
				dropArea.typeId = set.typeId;
				dropArea.isPlaceholder = set.isPlaceholder;
			});
		}

		dropAreas.push(...dayDropAreas);
	});

	return dropAreas;
}
